import * as lodash from 'lodash';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export function getNestedField<
  T extends object,
  K extends string | undefined | null | '',
>(obj: T, keys: K): DeepType<T, K> {
  let result = obj as any;
  if (!keys) {
    return obj as DeepType<T, K>;
  }
  const keyList = keys.split('.');
  for (let i = 0; i < keyList.length; i++) {
    const key = keyList[i];
    result = result[key];
    if (result === undefined) {
      result = '';
      break;
    }
  }
  return result;
}

export function setNestedField<
  T extends object,
  K extends string | undefined | null | '',
  V extends DeepType<K extends undefined | null | '' ? DeepPartial<T> : T, K>,
>(obj: T, keys: K, value: V): T {
  let current = obj;
  if (!keys) return lodash.merge(obj, value);
  const keysArray = keys.split('.');
  const lastIndex = keysArray.length - 1;
  for (let i = 0; i < lastIndex; i++) {
    const key = keysArray[i];
    if (!(key in current) || typeof current[key] !== 'object')
      current[key] = /^\d/.test(keysArray[i + 1]) ? [] : {};
    current = current[key];
  }
  current[keysArray[lastIndex]] = value;
  return obj;
}

export function addPontis(str: string) {
  const reversedStr = str.split('').reverse().join('');
  const regex = /(\d{3})(?=\d)/g;
  const reversedFormattedStr = reversedStr.replace(regex, '$1.');
  const formattedStr = reversedFormattedStr.split('').reverse().join('');
  return formattedStr;
}

export const removeDuplicates = <T = any>(obj: T[]): T[] => {
  return [...new Set(obj.map((val) => JSON.stringify(val)))].map((val) =>
    JSON.parse(val),
  );
};

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getFilterColumn = (
  column: string,
  searchFilter: { filter?: Filter[] },
  operator?: OPERATOR,
) =>
  searchFilter?.filter?.find(
    ({ column: col, operator: op }) =>
      col === column && (operator ? op === operator : true),
  )?.value ?? '';

export const setFilterColumn = (
  column: string,
  value: any,
  searchFilter: { filter?: Filter[] },
  operator: OPERATOR = 'LIKE',
  logicalOperator: LOGICAL_OPERATOR = 'AND',
  format?: string,
) => {
  return {
    ...searchFilter,
    ...(() =>
      searchFilter.filter
        ? {
            filter: [
              ...(() =>
                !['', null, undefined].includes(value)
                  ? [
                      {
                        column,
                        ...(() => (operator ? { operator } : {}))(),
                        ...(() =>
                          logicalOperator ? { logicalOperator } : {})(),
                        value,
                        ...(() =>
                          format ? { format } : {})(),
                      },
                    ]
                  : [])(),
              ...searchFilter.filter.filter(({ column: col, operator: op }) =>
                col === column ? (operator ? op !== operator : false) : true,
              ),
            ],
          }
        : {})(),
  };
};

export const getSortColumn = (
  column: string,
  searchFilter: { orderBy?: Sort[] },
) => searchFilter?.orderBy?.find(({ column: col }) => col === column)?.value;

export const setSortColumn = (
  column: string,
  value: any,
  searchFilter: { orderBy?: Sort[] },
) => {
  return {
    ...searchFilter,
    ...(() =>
      searchFilter.orderBy
        ? {
            orderBy: [
              ...(() =>
                !['', null, undefined].includes(value)
                  ? [
                      {
                        column,
                        value,
                      },
                    ]
                  : [])(),
              ...searchFilter.orderBy.filter(
                ({ column: col }) => col !== column,
              ),
            ],
          }
        : {})(),
  };
};

export const jsonToCsv = (json: any[]): string => {
  const headers = Object.keys(json[0]);
  const csvRows: any = [];
  csvRows.push(headers.join(','));
  for (const row of json) {
    csvRows.push(
      headers
        .map((header) =>
          JSON.stringify(row[header], (key, value) => value || ''),
        )
        .join(','),
    );
  }
  return csvRows.join('\n');
};

export const downloadCsv = (csv: string, filename: string): void => {
  const csvBlob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(csvBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const jsonToExcel = (json: any[], sheetname: string) => {
  const worksheet = XLSX.utils.json_to_sheet(json);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetname);
  return workbook;
};

export const downloadExcel = (
  workbook: XLSX.WorkBook,
  filename: string,
): void => {
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, filename);
};
